import React, { useState } from "react";
import { AnimatePresence, motion } from "framer-motion";
import {
  Building2, CalendarDays, Check, ClipboardList, Globe2, HeartPulse, KeyRound, LayoutDashboard, Pill, Smartphone, Stethoscope, Users,
} from "lucide-react";
import Reveal from "./Reveal";

const PORTALS = [
  {
    key: "central",
    icon: Globe2,
    tab: "Platform owner",
    who: "For health networks & groups",
    title: "Run every hospital from one control centre",
    body: "Onboard new hospitals and clinics, see every patient across the network, and give your team exactly the access they need.",
    points: [
      "Create and activate hospitals (tenants) in minutes",
      "Network-wide patient registry with search",
      "Sub-admins with role-based permissions",
      "Live view of locations, doctors and activity",
    ],
    accent: "from-indigo-500 to-brand-500",
    preview: [
      ["Hospitals", "12 active"],
      ["Patients", "48,210"],
      ["Admins", "6 with roles"],
    ],
  },
  {
    key: "admin",
    icon: Building2,
    tab: "Hospital admin",
    who: "For hospital & clinic management",
    title: "Set up your hospital exactly the way it works",
    body: "Locations, wards and beds, services, lab tests, packages and pharmacies — configured once and used everywhere.",
    points: [
      "Multi-location with buildings, floors, wards and bed rates",
      "Service, diagnostic and package price lists with GST",
      "Doctor rosters that turn into bookable slots",
      "Staff, roles and permissions per location",
    ],
    accent: "from-brand-500 to-sky-500",
    preview: [
      ["Locations", "3"],
      ["Beds", "46 in 5 wards"],
      ["Doctors on roster", "18"],
    ],
  },
  {
    key: "practitioner",
    icon: Stethoscope,
    tab: "Doctors & staff",
    who: "For doctors, nurses & front desk",
    title: "Everything the care team needs, on one screen",
    body: "A day view of every visit, one-click video consults, structured prescriptions, the inpatient bed board and billing at the counter.",
    points: [
      "Day list with check-in, confirm and complete",
      "Video consults with prescriptions in the same window",
      "Admit, transfer and discharge with a live bed board",
      "OPD and IPD bills, deposits and receipts",
    ],
    accent: "from-care-500 to-teal-500",
    preview: [
      ["Today", "42 visits"],
      ["Inpatients", "34 admitted"],
      ["Bills today", "₹1.84L"],
    ],
  },
  {
    key: "patient",
    icon: Smartphone,
    tab: "Patients",
    who: "For patients & families",
    title: "A modern front door for your patients",
    body: "Patients find a doctor, pick a slot, pay online and join video visits — and keep their prescriptions and records in one place.",
    points: [
      "Book in-person or video visits in under a minute",
      "One login for the whole family",
      "Prescriptions and visit history always available",
      "ABHA-linked health records",
    ],
    accent: "from-violet-500 to-fuchsia-500",
    preview: [
      ["Next visit", "Tue · 10:00"],
      ["Family", "4 members"],
      ["Records", "Prescriptions & labs"],
    ],
  },
];

const PREVIEW_ICONS = { central: [Globe2, Users, KeyRound], admin: [Building2, LayoutDashboard, CalendarDays], practitioner: [ClipboardList, HeartPulse, Pill], patient: [CalendarDays, Users, ClipboardList] };

const Portals = () => {
  const [active, setActive] = useState("practitioner");
  const p = PORTALS.find((x) => x.key === active);

  return (
    <section id="portals" className="relative py-24 sm:py-28">
      <div className="container-x">
        <Reveal className="mx-auto max-w-2xl text-center">
          <span className="eyebrow">Four connected portals</span>
          <h2 className="section-title">Built for everyone who keeps a hospital running</h2>
          <p className="mt-4 text-lg text-slate-600">
            Each team gets a workspace designed for their job — all sharing the same live data, so nothing is entered twice.
          </p>
        </Reveal>

        <Reveal delay={0.1} className="mt-12">
          <div role="tablist" aria-label="Portals" className="mx-auto grid max-w-3xl grid-cols-2 gap-2 rounded-2xl border border-slate-200 bg-slate-50 p-1.5 sm:grid-cols-4">
            {PORTALS.map((x) => (
              <button
                key={x.key}
                role="tab"
                aria-selected={active === x.key}
                onClick={() => setActive(x.key)}
                className={`relative flex items-center justify-center gap-2 rounded-xl px-3 py-2.5 text-sm font-semibold transition ${
                  active === x.key ? "text-ink-900" : "text-slate-500 hover:text-ink-900"
                }`}
              >
                {active === x.key && (
                  <motion.span layoutId="portal-pill" className="absolute inset-0 rounded-xl bg-white shadow-soft" transition={{ type: "spring", bounce: 0.2, duration: 0.5 }} />
                )}
                <x.icon className="relative h-4 w-4" />
                <span className="relative">{x.tab}</span>
              </button>
            ))}
          </div>
        </Reveal>

        <AnimatePresence mode="wait">
          <motion.div
            key={p.key}
            role="tabpanel"
            initial={{ opacity: 0, y: 16 }}
            animate={{ opacity: 1, y: 0 }}
            exit={{ opacity: 0, y: -10 }}
            transition={{ duration: 0.35 }}
            className="mt-10 grid items-center gap-10 rounded-3xl border border-slate-200 bg-white p-6 shadow-soft sm:p-10 lg:grid-cols-2"
          >
            <div>
              <p className="text-sm font-semibold text-brand-600">{p.who}</p>
              <h3 className="mt-2 text-2xl font-bold sm:text-3xl">{p.title}</h3>
              <p className="mt-4 text-slate-600">{p.body}</p>
              <ul className="mt-6 space-y-3">
                {p.points.map((pt) => (
                  <li key={pt} className="flex items-start gap-3">
                    <span className="mt-0.5 flex h-5 w-5 flex-shrink-0 items-center justify-center rounded-full bg-care-50 text-care-600">
                      <Check className="h-3.5 w-3.5" />
                    </span>
                    <span className="text-slate-700">{pt}</span>
                  </li>
                ))}
              </ul>
            </div>
            <div className={`relative overflow-hidden rounded-2xl bg-gradient-to-br ${p.accent} p-6 sm:p-8`}>
              <div className="bg-grid absolute inset-0 opacity-20 [filter:invert(1)]" />
              <div className="relative rounded-xl bg-white/95 p-5 shadow-xl">
                <div className="flex items-center gap-3 border-b border-slate-100 pb-4">
                  <span className={`flex h-10 w-10 items-center justify-center rounded-xl bg-gradient-to-br ${p.accent} text-white`}>
                    <p.icon className="h-5 w-5" />
                  </span>
                  <div>
                    <p className="text-sm font-bold text-ink-900">{p.tab} portal</p>
                    <p className="text-xs text-slate-500">Illustrative overview</p>
                  </div>
                </div>
                <div className="mt-4 grid grid-cols-1 gap-3 sm:grid-cols-3">
                  {p.preview.map(([k, v], i) => {
                    const Icon = PREVIEW_ICONS[p.key][i];
                    return (
                      <div key={k} className="rounded-lg border border-slate-100 bg-slate-50 p-3">
                        <Icon className="h-4 w-4 text-slate-400" />
                        <p className="mt-2 text-[11px] text-slate-500">{k}</p>
                        <p className="text-sm font-bold text-ink-900">{v}</p>
                      </div>
                    );
                  })}
                </div>
                <div className="mt-4 space-y-2">
                  {[78, 56, 90].map((w, i) => (
                    <div key={i} className="flex items-center gap-3">
                      <span className="h-2 w-2 rounded-full bg-slate-300" />
                      <span className="h-2 flex-1 rounded-full bg-slate-100">
                        <span className={`block h-2 rounded-full bg-gradient-to-r ${p.accent}`} style={{ width: `${w}%` }} />
                      </span>
                    </div>
                  ))}
                </div>
              </div>
            </div>
          </motion.div>
        </AnimatePresence>
      </div>
    </section>
  );
};

export default Portals;
